import { useState, type ReactNode } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { Admin, setAdminPass, hasAdminPass } from '@/lib/api';

/**
 * Wraps admin routes. Prompts for ADMIN_PANEL_PASSWORD (if backend enforces it)
 * before rendering children. Once entered, password is kept in sessionStorage
 * and auto-injected into all /admin/* requests by api.ts interceptor.
 *
 * If the backend doesn't enforce a password, the very first /admin/users call
 * succeeds without X-Admin-Pass and we skip the modal.
 */
export function AdminGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [pass, setPass] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  // First load: try once without password — if backend allows, skip modal.
  if (!checked) {
    Admin.listUsers()
      .then(() => setUnlocked(true))
      .catch(() => {
        // requires admin pass
        if (hasAdminPass()) {
          // Stored but stale — clear it
          setAdminPass(null);
        }
      })
      .finally(() => setChecked(true));
  }

  if (!checked) {
    return <div className="p-8 text-slate-500">Yetki kontrol ediliyor…</div>;
  }

  if (unlocked) return <>{children}</>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setAdminPass(pass);
    try {
      await Admin.listUsers();
      setUnlocked(true);
    } catch (e: any) {
      setAdminPass(null);
      setErr(e?.response?.status === 403 ? 'Şifre yanlış' : (e.message ?? 'Hata'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="bg-surface rounded-xl shadow-md p-7 w-[380px] border border-slate-200"
      >
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="text-amber-600" size={22} />
          <h2 className="text-lg font-semibold">Yönetici Şifresi</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Yönetim paneline erişmek için ekstra şifre gerekli. (Tab kapatınca sıfırlanır.)
        </p>

        <input
          autoFocus
          type="password"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded mb-3 font-mono"
          placeholder="Yönetici şifresi"
        />

        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

        <button
          type="submit"
          disabled={!pass || busy}
          className="w-full py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white font-medium rounded"
        >
          {busy ? 'Doğrulanıyor…' : 'Aç'}
        </button>
      </form>
    </div>
  );
}
